// Lib
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

// Utils
import { getUserId } from '../../../../../common/users/utils/userPropertyUtils';

// Selectors
import notificationsUtilSelector from './notificationsUtilSelector';

// Actions
import { markAllNotificationsAsSeen } from '../../../../notifications/notificationActions';
import { userCheckedNotifications } from '../../../../user/currentUserActions';

// Components
import PopupEmptyContent from '../../../../components/popupPanel/PopupEmptyContent';
import NotificationsErrorMessage from './popup/error/NotificationsErrorMessage';
import NoNotificationsMessage from './popup/empty/NoNotificationsMessage';

// Types
import { ImmutableMap } from '../../../../../common/utils/immutableHelper';
import { ImMNCurrentUser } from '../../../../user/currentUserSelector';

interface NotificationsContentProps {
    currentUser: ImMNCurrentUser;
    notifications: ImmutableMap;
    isFetchingNotifications: boolean;
    isFetchedNotifications: boolean;
    hasNotificationsFetchError: boolean;
    dispatchMarkAllNotificationsAsSeen: () => void;
    previousLastCheckedNotifications: number;
    ContentComponent: React.FC<any>;
    dispatchUpdateLastCheckedTime: (currentUserId: string) => void;
}

const NotificationsContent = (props: NotificationsContentProps): ReactElement => {
    const {
        notifications,
        isFetchedNotifications,
        isFetchingNotifications,
        hasNotificationsFetchError,
        ContentComponent,
    } = props;

    if (notifications?.size) return <ContentComponent {...props} />;

    if (!isFetchedNotifications || (isFetchingNotifications && hasNotificationsFetchError)) {
        return <PopupEmptyContent showSpinner />;
    }

    if (hasNotificationsFetchError) {
        return <NotificationsErrorMessage {...props} />;
    }

    return <NoNotificationsMessage />;
};

const mapDispatchToProps = (dispatch: Function) => ({
    dispatchUpdateLastCheckedTime: (currentUserId: string) =>
        dispatch(userCheckedNotifications({ userId: currentUserId })),
    dispatchMarkAllNotificationsAsSeen: (userId: string) => dispatch(markAllNotificationsAsSeen(userId)),
});

const connector = connect(notificationsUtilSelector, mapDispatchToProps);

type Props = ConnectedProps<typeof connector> & {
    ContentComponent: React.FC<any>;
};

const NotificationsContentContainer = (props: Props): ReactElement => {
    const { currentUser, lastCheckedNotifications, dispatchUpdateLastCheckedTime, dispatchMarkAllNotificationsAsSeen } =
        props;

    const [previousLastCheckedNotifications] = useState(lastCheckedNotifications);

    const userId = getUserId(currentUser);

    useEffect(() => {
        dispatchUpdateLastCheckedTime(userId);

        return () => {
            // Update the last checked time on close too, to cover any notifications
            //  that came in while the panel was open
            dispatchUpdateLastCheckedTime(userId);
        };
    }, []);

    const dispatchMarkAllUserNotificationsAsSeen = useCallback(() => {
        dispatchMarkAllNotificationsAsSeen(userId);
    }, []);

    return (
        <NotificationsContent
            {...props}
            previousLastCheckedNotifications={previousLastCheckedNotifications}
            dispatchMarkAllNotificationsAsSeen={dispatchMarkAllUserNotificationsAsSeen}
        />
    );
};

export default connector(NotificationsContentContainer);
